import {Client} from 'paho-mqtt';
import {fetchMqttToken, msUntilRefresh} from '@/composables/mqttAuth';

// App-global "company data changed" subscriber. The API publishes c/{companyId}/reload
// whenever a device/asset/zone/config/setting is saved (see AppServiceProvider observers).
// Live.vue already reacts to it, but only while the Live page is mounted - it owns the
// shared device-stream client and disconnects it on unmount, so on every other route
// (Devices, Places, Monitoring, Activity) there was no connection and no refresh.
//
// This runs for the whole authenticated session (owned by the Auth layout) on its own
// lightweight connection subscribed to just the reload topic, so it never contends with
// Live's single onMessageArrived handler. It dispatches the same `company-data-reload`
// window event the page listeners already handle - each page debounces its own refetch.

let client = null;
let refreshTimer = null;
let reconnectTimer = null;
let attempts = 0;
let intentional = false;
let companyId = null;

function clearTimers() {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
}

function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    // Reconnect with a fresh token shortly before the current one expires. When
    // msUntilRefresh() is already 0 (token inside its 60s safety window) refresh
    // soon rather than defaulting 25 min out and letting the token lapse.
    const ms = msUntilRefresh();
    refreshTimer = setTimeout(connect, ms > 0 ? ms : 5000);
}

function scheduleReconnect() {
    if (intentional || reconnectTimer) return;
    attempts += 1;
    // Exponential backoff: 2s, 4s, 8s ... capped at 30s (mirrors the shared plugin).
    const delay = Math.min(30000, 1000 * Math.pow(2, attempts));
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (!intentional) connect();
    }, delay);
}

async function connect() {
    if (intentional || !companyId) return;
    // Capture the company this attempt is for. A circle switch (or hydrateProfile
    // shortly after login) can change the module-level companyId while we're
    // suspended on the token fetch below; without this capture a stale connect()
    // could resurrect a duplicate/orphaned client or subscribe to the new circle's
    // topic with the old circle's token (broker rejects it -> reload silently dead).
    const cid = companyId;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }

    let auth;
    try {
        auth = await fetchMqttToken();
    } catch (err) {
        console.error('Reload MQTT: token unavailable', err);
        if (!intentional && companyId === cid) scheduleReconnect();
        return;
    }
    // Bail if a switch/logout landed while the token was in flight - this attempt
    // is stale, and startCompanyReload has already stood up the correct client.
    if (intentional || companyId !== cid) return;

    const url = auth.wsUrl || process.env.VUE_APP_MQTT_URL;
    if (!url) return;

    try { if (client && client.isConnected()) client.disconnect(); } catch (e) { /* already down */ }

    const topic = `c/${cid}/reload`;
    const clientId = `reload-${Math.random().toString(16).slice(2)}`;
    const c = new Client(url, clientId);
    client = c;

    c.onConnectionLost = () => {
        // Only the current client should drive reconnects.
        if (!intentional && client === c && companyId === cid) scheduleReconnect();
    };
    c.onMessageArrived = (message) => {
        try {
            if (message.destinationName !== topic) return;
            // A circle-membership change rides the same reload topic but carries a
            // distinct action. If it targets this user they were just moved into a
            // circle - hand off to the Auth layout to switch + re-scope. Anything
            // else (incl. a plain reload) is a data refresh.
            let payload = null;
            try { payload = JSON.parse(message.payloadString || '{}'); } catch (e) { payload = null; }
            if (payload && (payload.action === 'circle_membership_changed'
                    || payload.action === 'circle_membership_removed')) {
                window.dispatchEvent(new CustomEvent('circle-membership-changed', {
                    detail: {
                        action: payload.action,
                        targetUserId: Number(payload.target_user_id),
                        companyId: Number(payload.company_id),
                    },
                }));
                return;
            }
            window.dispatchEvent(new CustomEvent('company-data-reload'));
        } catch (err) {
            console.error('Reload MQTT: message error', err);
        }
    };
    c.connect({
        // The JWT is presented as the MQTT username; the password is unused.
        userName: auth.token,
        password: 'jwt',
        useSSL: url.startsWith('wss://'),
        onSuccess: () => {
            // A stop/switch may have landed between connect() and this callback.
            if (intentional || client !== c || companyId !== cid) return;
            attempts = 0;
            try { c.subscribe(topic); } catch (e) { /* not connected */ }
            scheduleRefresh();
        },
        onFailure: (err) => {
            console.error('Reload MQTT: connect failed', err && err.errorMessage);
            if (!intentional && client === c && companyId === cid) scheduleReconnect();
        },
    });
}

function onVisibilityChange() {
    if (document.visibilityState === 'visible' && companyId && !intentional) {
        if (!client || !client.isConnected()) connect();
    }
}

// Start (or re-point) the subscriber for the given company. Safe to call repeatedly;
// a no-op when already connected for the same company, a clean restart on a circle switch.
export function startCompanyReload(cid) {
    if (!process.env.VUE_APP_MQTT_URL || !cid) return;
    if (companyId === cid && client && client.isConnected && client.isConnected()) return;
    if (companyId && companyId !== cid) stopCompanyReload();
    intentional = false;
    companyId = cid;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    connect();
}

export function stopCompanyReload() {
    intentional = true;
    clearTimers();
    attempts = 0;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (client) {
        try { client.disconnect(); } catch (e) { /* already down */ }
    }
    client = null;
    companyId = null;
}
